import React from 'react'
import Sticky from 'react-stickynode'
import classNames from 'classnames'
import { Pagination } from 'transfix-ui/components/Pagination'
import RadialSpinner from 'transfix-ui/components/RadialSpinner'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { Table, TableBody } from 'transfix-ui'
import { IPriceRequestDetailsFragment } from '../../graphql/PriceRequestDetailsFragment'
import { HistoricalQuotesRow } from './HistoricalQuotesRow'
import { HistoricalQuotesTableHeader } from './HistoricalQuotesTableHeader'
import { FocusedQuotePanel } from './focus-panel/FocusedQuotePanel'

interface IHistoricalQuotesTableProps {
  priceRequests: IPriceRequestDetailsFragment[]
  quotesPerPage: number
  totalCount: number
  priceRequestsLoading: boolean
  currentPage: number
  totalPages: number
  focusedQuoteId: string | null
  setFocusedQuoteId: React.Dispatch<React.SetStateAction<string | null>>
  handlePageChange: (pageNumber: number) => Promise<void>
}

const useStyles = makeStyles(theme => ({
  table: { borderCollapse: 'separate' },
  emptyRows: {
    height: '400px',
    display: 'flex',
    justifyContent: 'center',
    marginTop: '40px',
  },
  spinnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
}))

export function HistoricalQuotesTable({
  currentPage,
  focusedQuoteId,
  handlePageChange,
  priceRequests,
  priceRequestsLoading,
  quotesPerPage,
  setFocusedQuoteId,
  totalCount,
  totalPages,
}: IHistoricalQuotesTableProps) {
  const focusedQuote = priceRequests.filter(quote => quote.id === focusedQuoteId)[0]
  const isPanelDisplayed =
    focusedQuoteId !== null && focusedQuote !== null && focusedQuote !== undefined
  const tableContainerClass = classNames(
    'col-xs12',
    isPanelDisplayed ? 'col-md-5 col-lg-5 col-xl-5' : null
  )
  const classes = useStyles()

  return (
    <div>
      {priceRequestsLoading && (
        <div className={classes.spinnerContainer}>
          <div>
            <RadialSpinner position='relative' size='large' />
          </div>
          <div>
            <Typography variant='h2'>Quotes Loading...</Typography>
          </div>
        </div>
      )}
      <div className='row'>
        {!priceRequestsLoading && (
          <div className={tableContainerClass}>
            <Table className={classes.table}>
              <HistoricalQuotesTableHeader focusedQuoteId={focusedQuoteId} />
              <TableBody>
                {priceRequests.map(quote => {
                  return (
                    <HistoricalQuotesRow
                      key={`quote-${quote.id}`}
                      priceRequest={quote}
                      focusedQuoteId={focusedQuoteId}
                      setFocusedQuoteId={setFocusedQuoteId}
                    />
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {isPanelDisplayed && (
          <div className='col-xs-12 col-md-7 col-lg-7 col-xl-7'>
            <Sticky>
              <FocusedQuotePanel
                setFocusedQuoteId={setFocusedQuoteId}
                priceRequest={focusedQuote}
              />
            </Sticky>
          </div>
        )}
      </div>
      {!priceRequestsLoading && totalCount === 0 && (
        <div className={classes.emptyRows}>No Quotes Found</div>
      )}
      {totalCount > quotesPerPage && (
        <Box textAlign='center'>
          <Pagination
            numberOfPages={totalPages}
            currentPage={currentPage}
            displayPageNumbers={false}
            loading={priceRequestsLoading}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </div>
  )
}
